import React, { useState, useEffect } from 'react';
import logo from '../assets/footer-logo.png';
import { Link, useLocation } from 'react-router-dom';
import facebookIcon from '../assets/contact-us/facebook-app-symbol.png';
import instagramIcon from '../assets/contact-us/instagram.png';
import linkedinIcon from '../assets/contact-us/linkedin.png';

const Navbar = ({ currentBgColor }) => {
  const location = useLocation();
  const [showTopNav, setShowTopNav] = useState(false);
  const [showBottomNav, setShowBottomNav] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [mountAnimationDone, setMountAnimationDone] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const socialLinks = [
    { id: 'facebook', href: 'https://www.facebook.com/share/16VL31FUML/?mibextid=wwXIfr', icon: facebookIcon},
    { id: 'instagram', href: 'https://www.instagram.com/bigblue_inc?igsh=bXBkMnBjZzlwNWFi&utm_source=qr', icon: instagramIcon },
    { id: 'linkedin', href: 'https://www.linkedin.com/company/big-blue-inc/', icon: linkedinIcon },
  ];

  // Utility functions for color interpolation
  const hexToRgb = (hex) => {
    const bigint = parseInt(hex.slice(1), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const interpolateColor = (c1, c2, factor) => {
    return {
      r: Math.round(c1.r + (c2.r - c1.r) * factor),
      g: Math.round(c1.g + (c2.g - c1.g) * factor),
      b: Math.round(c1.b + (c2.b - c1.b) * factor),
    };
  };

  const rgbToHex = ({ r, g, b }) => {
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  };

  // Calculate luminance for text color decision
  const getLuminance = ({ r, g, b }) => {
    return 0.299 * r + 0.587 * g + 0.114 * b;
  };

  // Compute navbar background and text color
  const color1 = hexToRgb('#02044f'); // Dark blue
  const color2 = hexToRgb('#86cde9'); // Light blue
  const currentColor = hexToRgb(currentBgColor || '#02044f');

  // Calculate progress of currentBgColor between color1 and color2
  const progress = Math.min(
    Math.max(
      ((currentColor.r - color1.r) / (color2.r - color1.r) +
        (currentColor.g - color1.g) / (color2.g - color1.g) +
        (currentColor.b - color1.b) / (color2.b - color1.b)) /
      3,
      0
    ),
    1
  );

  // Interpolate navbar background in opposite direction
  const navBgColorRgb = interpolateColor(color2, color1, progress);
  const navBgColor = rgbToHex(navBgColorRgb);

  // Text color based on navbar background luminance
  const luminance = getLuminance(navBgColorRgb);
  const navTextColor = luminance < 128 ? '#ffffff' : '#02044f';
  const navBarIconColor = navTextColor === '#ffffff' ? '#02044f' : '#ffffff';


  useEffect(() => {
    const currentY = window.scrollY;
    setLastScrollY(currentY);
    setShowTopNav(currentY === 0);

    requestAnimationFrame(() => {
      setMountAnimationDone(true);
      setInitialized(true);
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!initialized) return;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const direction = currentY > lastScrollY ? 'down' : 'up';
      setLastScrollY(currentY);

      const footer = document.querySelector('footer');
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        setFooterVisible(footerTop < window.innerHeight);
      }

      if (currentY === 0) {
        setShowTopNav(true);
        setShowBottomNav(false);
      } else if (direction === 'down') {
        setShowTopNav(false);
        setShowBottomNav(false);
      } else if (direction === 'up') {
        setShowTopNav(false);
        setShowBottomNav(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, initialized]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile menu
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLinkClick = () => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full flex justify-between items-center px-4 sm:px-8 lg:py-2 lg:px-10 bg-transparent">
      {/* Logo */}
      <div
        className={`
          transition-all duration-[1000ms] ease-out transform
          ${(!mountAnimationDone || showTopNav) ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
          ${!mountAnimationDone ? '-translate-y-32 opacity-0' : ''}
        `}
      >
        <Link to="/" onClick={handleLinkClick}>
          <img src={logo} alt="Logo" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-32 lg:h-32" />
        </Link>
      </div>

      {/* Mobile Hamburger */}
      <button
        className="lg:hidden flex flex-col justify-center items-center w-10 h-10 z-50 "
        style={{ backgroundColor: 'transparent' }}
        onClick={toggleMenu} // Fixed: Added onClick handler
      >
        <span
          className={`block w-7 h-1 mb-1.5 rounded transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''
            }`}
          style={{ backgroundColor: navBarIconColor }}
        />
        <span
          className={`block w-7 h-1 mb-1.5 rounded transition-all duration-300 ${menuOpen ? 'opacity-0' : ''
            }`}
          style={{ backgroundColor: navBarIconColor }}
        />
        <span
          className={`block w-7 h-1 rounded transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-3' : ''
            }`}
          style={{ backgroundColor: navBarIconColor }}
        />
      </button>

      {/* Desktop Nav */}
      <nav
        className={`
          hidden lg:block
          transition-all duration-[1000ms] ease-out transform rounded-full
          ${showTopNav ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
          ${!mountAnimationDone ? 'scale-0 opacity-0' : 'scale-100'}
        `}
        style={{ backgroundColor: navBgColor, color: navTextColor }}
      >
        <div className="flex justify-center space-x-8 text-lg lg:text-xl px-9 py-5 rounded-full shadow-lg">
          <Link
            to="/"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            Home
          </Link>
          <Link
            to="/Services"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            Services
          </Link>
          <Link
            to="/About"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            About Us
          </Link>
          <Link
            to="/Blogs"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            Blogs
          </Link>
        </div>
      </nav>

      {/* Contact Button */}
      <div
        className={`
          hidden lg:flex transition-all duration-[1000ms] ease-out transform
          ${(!mountAnimationDone || showTopNav) ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
          ${!mountAnimationDone ? '-translate-y-32 opacity-0' : ''}
        `}
      >
        <div className="h-max py-4 flex items-center space-x-4 border-2 border-white rounded-full transition-colors duration-300 hover:bg-white">
          <Link
            to="/Contact"
            onClick={handleLinkClick}
            className="lg:text-xl text-white bg-transparent px-8 transition-colors duration-300 hover:text-blue-900"
          >
            Contact Us
          </Link>
        </div>
      </div>

      {/* SOCIAL LINKS BOTTOM LEFT */}
      <div className={`fixed bottom-4 left-4 z-40 flex flex-col space-y-3 ${!footerVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'}`}>
        {socialLinks.map(link => (
          <a
            key={link.id}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group"
          >
            <img
              src={link.icon}
              alt={link.id}
              className="w-8 h-8 sm:w-10 sm:h-10  p-1 transition-transform duration-300 transform group-hover:scale-110"
              style={{ filter: navTextColor === '#ffffff' ? 'invert(1)' : 'invert(0)' }}
            />
          </a>
        ))}
      </div>
      {/* Bottom Floating Navbar */}
      <nav
        className={`hidden lg:block fixed bottom-1 left-1/2 transform -translate-x-1/2 transition-all duration-500 ${showBottomNav && !footerVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
          } px-7 py-4 rounded-full shadow-lg`}
        style={{ backgroundColor: navBgColor, color: navTextColor }}
      >
        <div className="flex justify-center space-x-8 text-lg lg:text-xl">
          <Link
            to="/"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            Home
          </Link>
          <Link
            to="/Services"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            Services
          </Link>
          <Link
            to="/About"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            About Us
          </Link>
          <Link
            to="/Blogs"
            onClick={handleLinkClick}
            className="hover:text-blue-400 transition-colors duration-300"
            style={{ color: navTextColor }}
          >
            Blogs
          </Link>
        </div>
      </nav>

      {/* Mobile Fullscreen Menu */}
      <div
        className={`lg:hidden fixed top-0 left-0 w-full h-full flex flex-col items-center justify-center space-y-8 z-40 transition-transform duration-300 ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: navBgColor, pointerEvents: menuOpen ? 'auto' : 'none' }}
      >
        <div className="flex flex-col items-center space-y-8 text-2xl">
          <Link
            to="/"
            onClick={handleLinkClick}
            className="hover:text-blue-400"
            style={{ color: navTextColor }}
          >
            Home
          </Link>
          <Link
            to="/Services"
            onClick={handleLinkClick}
            className="hover:text-blue-400"
            style={{ color: navTextColor }}
          >
            Services
          </Link>
          <Link
            to="/About"
            onClick={handleLinkClick}
            className="hover:text-blue-400"
            style={{ color: navTextColor }}
          >
            About Us
          </Link>
          <Link
            to="/Blogs"
            onClick={handleLinkClick}
            className="hover:text-blue-400"
            style={{ color: navTextColor }}
          >
            Blogs
          </Link>
          <Link
            to="/Contact"
            onClick={handleLinkClick}
            className="border-2 rounded-3xl px-8 py-2 mt-4"
            style={{ color: navTextColor, borderColor: navTextColor }}
          >
            Contact Us
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;